/**
 * The declined-card notice above the bill (handover L6, locked copy): the
 * newest open invoice Stripe has already failed to charge, what is still due
 * and when Stripe tries again, with the two ways out.
 *
 *   - Pay now: that invoice's Stripe-hosted page, in a new tab (never framed).
 *     The tab re-reads Stripe when the customer comes back to it (`onPayNow`),
 *     so a paid invoice does not keep its banner for the 10 minute cache.
 *   - Change card: the Stripe customer portal (`useBillingPortal`).
 *
 * The amount is what is still due, tax included, so it says "inc GST" rather
 * than the bill's "ex GST" (L7: money always says which it is).
 *
 * `role="alert"`: it is an error state that needs action. Red tint with an
 * icon and words, never colour alone (§16).
 */
import { useTranslation } from 'react-i18next';
import { CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBillingFormat } from '../hooks/useBillingFormat';
import type { BillingPortal } from '../hooks/useBillingPortal';
import { PRIMARY_ACTION_CLASS, SECONDARY_ACTION_CLASS } from '../lib/billing-ui';
import type { ActiveOverview } from '../lib/billing-view';

interface DeclinedNoticeProps {
  failed: NonNullable<ActiveOverview['failedInvoice']>;
  portal: BillingPortal;
  /** Pay now was clicked: re-read Stripe when the customer returns to this tab. */
  onPayNow: () => void;
}

export function DeclinedNotice({ failed, portal, onPayNow }: DeclinedNoticeProps) {
  const { t } = useTranslation('billing');
  const fmt = useBillingFormat();
  const amount = t('money.incGst', { amount: fmt.money(failed.amountCents, failed.currency) });
  const next = failed.nextAttemptAt ? fmt.day(failed.nextAttemptAt) : null;
  const body = failed.number
    ? next
      ? t('declined.body', { number: failed.number, amount, next })
      : t('declined.bodyNoRetry', { number: failed.number, amount })
    : next
      ? t('declined.bodyNoNumber', { amount, next })
      : t('declined.bodyNoNumberNoRetry', { amount });

  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-x-3 gap-y-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-3 sm:px-4"
    >
      <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
      <div className="min-w-0 flex-1 basis-64 space-y-0.5">
        <p className="text-sm font-semibold">
          {/* failedAt is the invoice's finalize time (Stripe charges right after); always set in practice. */}
          {t('declined.title', { date: fmt.day(failed.failedAt) })}
        </p>
        <p className="max-w-prose text-sm text-foreground/80">{body}</p>
      </div>
      <div className="ml-7 flex flex-wrap gap-2 sm:ml-auto">
        {failed.hostedUrl && (
          <Button asChild className={PRIMARY_ACTION_CLASS}>
            <a href={failed.hostedUrl} target="_blank" rel="noopener noreferrer" onClick={onPayNow}>
              {t('payNow')}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">{t('opensStripe')}</span>
            </a>
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className={SECONDARY_ACTION_CLASS}
          onClick={portal.open}
          disabled={portal.busy}
          aria-busy={portal.busy || undefined}
        >
          {portal.busy && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {t('changeCard')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">{t('opensStripe')}</span>
        </Button>
      </div>
    </div>
  );
}
